import { awaitableTemplate } from '../template-processor';
import { customElement, property, state } from 'lit/decorators.js';
import { DevelopmentError } from '../../development-error';
import { disableUI, enableUI } from '../../ui-lock';
import { EAbort } from '../../abort';
import { EventTemplate } from '../../../interop/webmodule-interop';
import { getInternalId } from '../databinding/databinding';
// eslint-disable-next-line import/named
import { html, nothing, PropertyValueMap, TemplateResult } from 'lit';
import { injectTabManagerStyles } from './tab-manager.styles';
import { showError } from '../show-error';
import { tabActionTemplate } from './tab-manager-buttons';
import { userPreferencesChangedEventName, userUIPreferences } from '../../context/user-preferences';
import { ViewBase } from '../view-base';
import type {
  TabAction,
  TabCloseEvent,
  TabManagerEvents,
  TabManagerOptions,
  TabPage,
  TabSnippet
} from './tab-manager-types';
import type { TabActionRenderContext } from './tab-manager-buttons';
import type {
  WebmoduleTab,
  WebmoduleTabBeforeShowEvent,
  WebmoduleTabGroup
} from '../../../components/src/webmodule-components';

/** Internal record pairing a page with its stable panel identity. */
interface TabEntry {
  page: TabPage;
  panelName: string;
  /** Set once the page has been activated; drives the `lazy` option. */
  contentRendered: boolean;
}

/**
 * A page control built on the design-system tab components (`webmodule-tab-group`,
 * `webmodule-tab`, `webmodule-tab-panel`). It owns the app semantics the raw
 * components do not: async switch/close guards, page lifecycle events, URL hash
 * deep-linking, UI locking during transitions, and the action button bar.
 *
 * This is the successor to the legacy {@link PageControl} (`wm-pagecontrol`);
 * consumers migrate page by page. The mapping from the legacy API:
 *
 * | Legacy (PageControl)             | TabManager                          |
 * |----------------------------------|-------------------------------------|
 * | `PageManager`                    | `TabPage`                           |
 * | `content(index)`                 | `content(page)`                     |
 * | `pageFragment`                   | `fragment`                          |
 * | `hasDelete()`                    | `closable`                          |
 * | `onDelete`                       | `onClose`                           |
 * | `isHidden` / `isTabHidden`       | `hidden` / `tabHidden`              |
 * | `buttonMenu`                     | `secondaryButtons` / `primaryButtons` |
 * | `pageButtonLocation`             | (removed; pick the region per page) |
 * | `callbacks` (PageCallback)       | `connect(host)` (TabPageHost)       |
 * | `pageInitializer`                | `options.pages`                     |
 * | `defaultTabIndex`                | `options.initialIndex`              |
 * | `plusPage` (MenuIconOption)      | `options.addAction` (TabAction)     |
 * | `menuIcons`                      | `options.actions`                   |
 * | `events.onDeleteTab`             | `events.onTabClosed`                |
 * | `setActiveTabIndex(i)`           | `setActiveIndex(i)`                 |
 * | `setActiveTabByHash(h)`          | `setActiveByFragment(h)`            |
 * | `canCloseAllTabs()`              | `canCloseAllPages()`                |
 * | `setMenuIcons(icons)`            | `setActions(actions)`               |
 *
 * New over the legacy control: `placement` (tab strip orientation), `activation`,
 * `noScrollControls`, `lazy` panel rendering, and a configurable button bar
 * location (`buttonBar: 'footer' | 'nav' | 'none'`).
 *
 * The button bar has three regions (mirroring the quote page footer):
 * - secondary (start side): the active page's `secondaryButtons`
 * - primary (end side): the active page's `primaryButtons`
 * - overall (far end): always rendered regardless of the active page, in the
 *   order `addAction`, `actions`, `overallButtons`
 */
@customElement('wm-tab-manager')
export class TabManager extends ViewBase {
  /** Tab strip placement, forwarded to webmodule-tab-group. Defaults to the user's preference. */
  @property({ attribute: false }) placement: 'top' | 'bottom' | 'start' | 'end' = 'top';
  /** Visual style ('classic' folder tabs or 'chip' + pill), forwarded to webmodule-tab-group. Defaults to the user's preference. */
  @property({ attribute: false }) variant: 'classic' | 'chip' = 'classic';
  /** Keyboard activation mode, forwarded to webmodule-tab-group. */
  @property({ attribute: false }) activation: 'auto' | 'manual' = 'auto';
  /** Disable the tab strip scroll buttons, forwarded to webmodule-tab-group. */
  @property({ attribute: false }) noScrollControls = false;
  /** Hide the tab strip while only one tab is visible. */
  @property({ attribute: false }) hideSingleTab = false;
  /** Where the button bar renders. */
  @property({ attribute: false }) buttonBar: 'footer' | 'nav' | 'none' = 'footer';
  /** The add/"plus" action in the overall region; runs the active page's canLeave before executing. */
  @property({ attribute: false }) addAction?: TabAction;
  /** Action buttons in the overall region, rendered irrespective of the active page. */
  @property({ attribute: false }) actions?: TabAction[];
  /** Free-form overall content, rendered irrespective of the active page after the actions. */
  @property({ attribute: false }) overallButtons?: TabSnippet;

  protected events: TabManagerEvents | null;

  @state()
  private entries: TabEntry[] = [];
  @state()
  private activeEntry: TabEntry | null = null;
  @state()
  private lockCount = 0;
  private firstLoaded = false;
  private readonly lazy: boolean;
  // Pages that do not pin placement/variant follow the user preference, live.
  private readonly followPreferredPlacement: boolean;
  private readonly followPreferredVariant: boolean;

  constructor(options: TabManagerOptions = {}) {
    super();
    injectTabManagerStyles();
    this.followPreferredPlacement = options.placement === undefined;
    this.followPreferredVariant = options.variant === undefined;
    this.placement = options.placement ?? userUIPreferences.current.tabPlacement;
    this.variant = options.variant ?? userUIPreferences.current.tabVariant;
    this.activation = options.activation ?? 'auto';
    this.noScrollControls = options.noScrollControls ?? false;
    this.hideSingleTab = options.hideSingleTab ?? false;
    this.buttonBar = options.buttonBar ?? 'footer';
    this.addAction = options.addAction;
    this.actions = options.actions;
    this.overallButtons = options.overallButtons;
    this.events = options.events ?? null;
    this.lazy = options.lazy ?? false;

    const pages = typeof options.pages === 'function' ? options.pages() : (options.pages ?? []);
    this.entries = pages.map(page => this.createEntry(page));

    const initialIndex = options.initialIndex ?? 0;
    if (initialIndex >= 0 && initialIndex < this.entries.length) {
      this.activeEntry = this.entries[initialIndex];
      this.activeEntry.contentRendered = true;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(userPreferencesChangedEventName, this.applyPreferredTabDefaults);
    // resync: a change broadcast while this element was detached was missed
    this.applyPreferredTabDefaults();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(userPreferencesChangedEventName, this.applyPreferredTabDefaults);
  }

  private readonly applyPreferredTabDefaults = () => {
    if (this.followPreferredPlacement) this.placement = userUIPreferences.current.tabPlacement;
    if (this.followPreferredVariant) this.variant = userUIPreferences.current.tabVariant;
  };

  get pages(): readonly TabPage[] {
    return this.entries.map(entry => entry.page);
  }

  get activePage(): TabPage | null {
    return this.activeEntry?.page ?? null;
  }

  get activeIndex(): number {
    return this.activeEntry ? this.entries.indexOf(this.activeEntry) : -1;
  }

  get pageCount(): number {
    return this.entries.length;
  }

  private get uiLocked(): boolean {
    return this.lockCount > 0;
  }

  private get tabGroup(): WebmoduleTabGroup | null {
    return this.querySelector<WebmoduleTabGroup>(':scope > webmodule-tab-group');
  }

  addPage(page: TabPage, activate = true) {
    const entry = this.createEntry(page);
    this.entries = [...this.entries, entry];
    if (activate) {
      // fire and forget like the legacy addPage; callers that need the result use setActivePage
      void this.activateEntry(entry);
    }
  }

  async closePage(page: TabPage, force = false): Promise<boolean> {
    const entry = this.entries.find(x => x.page === page);
    if (!entry) return false;
    return await this.closeEntry(entry, force);
  }

  async setActivePage(page: TabPage): Promise<boolean> {
    const entry = this.entries.find(x => x.page === page);
    if (!entry) throw new Error(`invalid page object passed to setActivePage`);
    return await this.activateEntry(entry);
  }

  async setActiveIndex(index: number, force?: boolean): Promise<boolean> {
    if (index === -1) {
      // deactivate all pages; matches the legacy control's -1 support
      this.activeEntry = null;
      return true;
    }
    this.rangeTest(index);
    return await this.activateEntry(this.entries[index], force);
  }

  async setActiveByFragment(hashId: string): Promise<boolean> {
    if (hashId.at(0) === '#') {
      hashId = hashId.substring(1);
    }
    const entry = this.entries.find(x => x.page.fragment === hashId);
    if (!entry) return false;
    return await this.activateEntry(entry);
  }

  async applyWindowHash(): Promise<boolean> {
    return await this.setActiveByFragment(window.location.hash);
  }

  /** Close every page in order. Stops and returns false if a page refuses to close. */
  async clear(): Promise<boolean> {
    while (this.entries.length > 0) {
      if (!(await this.closeEntry(this.entries[0]))) return false;
    }
    return true;
  }

  /** Test whether all pages would allow closing (closable pages via canClose, the active page via canLeave). */
  async canCloseAllPages(): Promise<boolean> {
    let result = true;
    for (const entry of this.entries) {
      const page = entry.page;
      if (this.isClosable(page)) {
        if (page.canClose && !(await page.canClose(page))) result = false;
      } else if (entry === this.activeEntry) {
        if (page.canLeave && !(await page.canLeave(page))) result = false;
      }
    }
    return result;
  }

  setActions(actions: TabAction[]) {
    this.actions = actions;
  }

  async runOnEnterEvent() {
    const page = this.activePage;
    if (page) await page.onEnter?.(page);
  }

  async runOnAfterEnterEvent() {
    const page = this.activePage;
    if (page) await page.onAfterEnter?.(page);
  }

  async dispose(): Promise<void> {
    for (const entry of this.entries) {
      await entry.page.dispose?.(entry.page);
    }
    this.entries = [];
    this.activeEntry = null;
    await super.dispose();
  }

  willUpdate(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    super.willUpdate(changedProperties);
    // eslint-disable-next-line wc/no-self-class
    this.classList.add('tab-manager');
    // eslint-disable-next-line wc/no-self-class
    this.classList.toggle('tab-manager--hide-nav', this.computeHideNav());
    // eslint-disable-next-line wc/no-self-class
    this.classList.toggle('tab-manager--nav-buttons', this.buttonBar === 'nav' && this.hasButtonBarContent);
  }

  protected template(): EventTemplate {
    const visibleEntries = this.entries.filter(entry => !entry.page.hidden?.());
    const navActions =
      this.buttonBar === 'nav' && this.hasButtonBarContent
        ? html` <div slot="nav" class="tab-manager__nav-actions">${this.buttonBarRegionsTemplate('bottom')}</div>`
        : nothing;
    return html`
      <webmodule-tab-group
        placement=${this.placement}
        variant=${this.variant}
        activation=${this.activation}
        ?no-scroll-controls=${this.noScrollControls}
        @webmodule-tab-before-show=${this.tabBeforeShowEvent}
        @webmodule-remove=${this.tabRemoveEvent}
      >
        ${visibleEntries.map(entry => this.tabTemplate(entry))} ${navActions}
        ${visibleEntries.map(entry => this.panelTemplate(entry))}
      </webmodule-tab-group>
      ${this.buttonBar === 'footer' ? this.footerTemplate() : nothing}
    `;
  }

  protected async firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
    await super.firstUpdated(_changedProperties);
    try {
      const entry = this.activeEntry;
      if (entry) {
        await entry.page.onEnter?.(entry.page);
        // Re-check after every await: a disconnect (router navigation) disposes
        // the pages mid-load, and a hash deep-link can switch the active page;
        // the captured entry must not receive further lifecycle calls then.
        if (this.activeEntry !== entry) return;
        this.requestUpdate();
        await this.updateComplete;
        if (this.activeEntry !== entry) return;
        await entry.page.onAfterEnter?.(entry.page);
      }
    } catch (e) {
      if (!(e instanceof EAbort)) await showError(e as Error);
    } finally {
      this.firstLoaded = true;
    }
  }

  protected createEntry(page: TabPage): TabEntry {
    const entry: TabEntry = {
      page,
      panelName: `wm-tm-panel-${getInternalId()}`,
      contentRendered: false
    };
    page.connect?.({
      manager: this,
      refresh: () => this.requestUpdate(),
      close: async (force = false) => await this.closePage(page, force)
    });
    return entry;
  }

  private tabTemplate(entry: TabEntry): TemplateResult {
    const page = entry.page;
    return html` <webmodule-tab
      slot="nav"
      panel=${entry.panelName}
      ?active=${entry === this.activeEntry}
      ?closable=${this.isClosable(page)}
      ?data-tab-hidden=${page.tabHidden?.() ?? false}
    >
      ${awaitableTemplate(page.caption?.() ?? '?')}
    </webmodule-tab>`;
  }

  private panelTemplate(entry: TabEntry): TemplateResult {
    const renderContent = !this.lazy || entry.contentRendered;
    return html` <webmodule-tab-panel name=${entry.panelName} ?active=${entry === this.activeEntry}>
      ${renderContent ? awaitableTemplate(entry.page.content(entry.page)) : nothing}
    </webmodule-tab-panel>`;
  }

  private get hasButtonBarContent(): boolean {
    const activePage = this.activeEntry?.page;
    return !!(
      this.addAction ||
      this.actions?.length ||
      this.overallButtons ||
      activePage?.secondaryButtons ||
      activePage?.primaryButtons
    );
  }

  private footerTemplate(): TemplateResult | typeof nothing {
    if (!this.hasButtonBarContent) return nothing;
    return html` <div class="tab-manager__footer">${this.buttonBarRegionsTemplate('top')}</div>`;
  }

  /**
   * The three button bar regions: the active page's secondary buttons on the start
   * (left) side, then right-aligned its primary buttons with the overall region
   * (rendered irrespective of the active page) directly next to them.
   * Empty regions are omitted so they add no stray gaps.
   */
  private buttonBarRegionsTemplate(side: 'top' | 'bottom'): TemplateResult {
    const activePage = this.activeEntry?.page;
    const hasOverall = !!(this.addAction || this.actions?.length || this.overallButtons);
    return html`
      ${activePage?.secondaryButtons
        ? html`<div class="tab-manager__buttons-secondary">${awaitableTemplate(activePage.secondaryButtons())}</div>`
        : nothing}
      ${activePage?.primaryButtons
        ? html`<div class="tab-manager__buttons-primary">${awaitableTemplate(activePage.primaryButtons())}</div>`
        : nothing}
      ${hasOverall
        ? html`<div class="tab-manager__buttons-overall">${this.overallRegionTemplate(side)}</div>`
        : nothing}
    `;
  }

  private overallRegionTemplate(side: 'top' | 'bottom'): TemplateResult {
    const context: TabActionRenderContext = {
      locked: this.uiLocked,
      dropdownPlacement: side,
      run: action => this.runAction(action)
    };
    const addTemplate = this.addAction
      ? tabActionTemplate(this.addAction, {
          ...context,
          dropdownPlacement: side === 'top' ? 'top-start' : 'bottom-start',
          run: action => this.runAddAction(action)
        })
      : nothing;
    // Mirror the actions: the legacy .page-nav-buttons-right laid out
    // [plusPage, ...menuIcons] under flex row-reverse, so the first action
    // pushed sat right-most and the add button right-most of all. Callers keep
    // pushing in that priority order.
    const actions = [...(this.actions ?? [])].reverse();
    return html`${actions.map(action => tabActionTemplate(action, context))}${addTemplate}
    ${awaitableTemplate(this.overallButtons?.())}`;
  }

  private tabBeforeShowEvent = (e: WebmoduleTabBeforeShowEvent) => {
    // a nested webmodule-tab-group inside a panel bubbles the same event; leave it alone
    if (e.target !== this.tabGroup) return;
    // we always take over the switch so the async guards can run first
    e.preventDefault();
    e.stopPropagation();
    if (this.uiLocked) return;
    const entry = this.entries.find(x => x.panelName === e.detail.name);
    if (entry) void this.activateEntry(entry);
  };

  private tabRemoveEvent = (e: Event) => {
    const tab = e.target as WebmoduleTab | null;
    const entry = this.entries.find(x => x.panelName === tab?.panel);
    if (!entry) return; // a nested tab group's tab; not ours
    e.stopPropagation();
    if (this.uiLocked) return;
    void this.closeEntry(entry);
  };

  private isClosable(page: TabPage): boolean {
    return typeof page.closable === 'function' ? page.closable() : (page.closable ?? false);
  }

  private computeHideNav(): boolean {
    if (!this.hideSingleTab) return false;
    const visibleTabs = this.entries.filter(entry => !entry.page.hidden?.() && !entry.page.tabHidden?.());
    return visibleTabs.length <= 1;
  }

  private async activateEntry(target: TabEntry, force = false): Promise<boolean> {
    const previous = this.activeEntry;
    if (target === previous && !force) return true;
    this.lockUI(true);
    try {
      let switched = false;
      try {
        const canLeave = !this.firstLoaded || target === previous || (await this.canLeavePage(previous?.page ?? null));
        if (!canLeave) return false;
        this.activeEntry = target;
        target.contentRendered = true;
        switched = true;
        await this.requestUpdateAndWait();
        await this.showActiveTab();
        await target.page.onEnter?.(target.page);
        this.pushFragment(target.page);
        await target.page.onAfterEnter?.(target.page);
        this.events?.onTabChanged?.(target.page, this.activeIndex);
        return true;
      } catch (e) {
        // EAbort is the silent flow-control revert; anything else surfaces.
        // Errors must not escape: click paths fire-and-forget this promise, so
        // a rethrow would die as an unhandled rejection with the manager stuck
        // on the broken page.
        if (switched) {
          this.activeEntry = previous;
          await this.requestUpdateAndWait();
          await this.showActiveTab();
        }
        if (!(e instanceof EAbort)) await showError(e as Error);
        return false;
      }
    } finally {
      this.lockUI(false);
    }
  }

  private async closeEntry(entry: TabEntry, force = false): Promise<boolean> {
    const index = this.entries.indexOf(entry);
    if (index < 0) return false;
    const wasActive = entry === this.activeEntry;
    this.lockUI(true);
    try {
      const page = entry.page;
      // Guard phase: a refusal - or an error - here keeps the page open.
      try {
        if (!force) {
          const canClose = page.canClose ? await page.canClose(page) : true;
          if (!canClose) return false;
        }
        await page.onClose?.(page);
      } catch (e) {
        if (!(e instanceof EAbort)) await showError(e as Error);
        return false;
      }
      this.entries = this.entries.filter(x => x !== entry);

      let newIndex: number;
      if (!wasActive) {
        newIndex = this.activeIndex;
      } else {
        // activate the neighbor that slid into the closed page's slot, or the new last page
        newIndex = Math.min(index, this.entries.length - 1);
      }

      // Removal phase: the page is gone. Errors surface but must not skip the
      // bookkeeping and repaint below, or the closed tab lingers in the DOM.
      try {
        const eventData: TabCloseEvent = { page, index, newIndex };
        await this.events?.onTabClosed?.(eventData);
        if (eventData.newIndex !== newIndex && eventData.newIndex >= 0 && eventData.newIndex < this.entries.length) {
          newIndex = eventData.newIndex;
        }
      } catch (e) {
        if (!(e instanceof EAbort)) await showError(e as Error);
      }

      const previousActive = this.activeEntry;
      this.activeEntry = newIndex >= 0 ? (this.entries[newIndex] ?? null) : null;
      if (this.activeEntry) this.activeEntry.contentRendered = true;

      try {
        await page.dispose?.(page);
      } catch (e) {
        if (!(e instanceof EAbort)) await showError(e as Error);
      }
      await this.requestUpdateAndWait();
      await this.showActiveTab();
      // Only re-run the enter pipeline when the close changed what is displayed
      // (the active page closed, or an onTabClosed override moved the selection);
      // closing a background tab leaves the active page untouched.
      if (this.activeEntry !== previousActive) await this.reValidateActive();
      return true;
    } finally {
      this.lockUI(false);
    }
  }

  /** Re-run the active page's enter events after a close changed what is displayed. */
  private async reValidateActive(): Promise<void> {
    const entry = this.activeEntry;
    if (!entry) return;
    try {
      await entry.page.onEnter?.(entry.page);
      await entry.page.onAfterEnter?.(entry.page);
    } catch (e) {
      if (!(e instanceof EAbort)) {
        await showError(e as Error);
        return;
      }
      const first = this.entries[0] ?? null;
      if (first === entry) return; // the aborting page IS the fallback; nowhere to go
      this.activeEntry = first;
      if (first) {
        first.contentRendered = true;
        await this.requestUpdateAndWait();
        await this.showActiveTab();
        try {
          await first.page.onEnter?.(first.page);
          await first.page.onAfterEnter?.(first.page);
        } catch (fallbackError) {
          // give up quietly on a second EAbort; surface anything else
          if (!(fallbackError instanceof EAbort)) await showError(fallbackError as Error);
        }
      }
    }
  }

  /**
   * Request a render and wait for it. A never-connected element never updates
   * (Lit enables updating on first connect), so awaiting updateComplete there
   * would hang forever - and with it the UI lock. Deep-linking hits this:
   * pages call applyWindowHash while the manager is still detached.
   */
  private async requestUpdateAndWait(): Promise<void> {
    this.requestUpdate();
    if (this.hasUpdated || this.isConnected) await this.updateComplete;
  }

  /** Sync the tab group's internal active tab (and indicator) with our active entry. */
  private async showActiveTab(): Promise<void> {
    const group = this.tabGroup;
    const entry = this.activeEntry;
    if (!group || !entry) return;
    await group.updateComplete;
    group.show(entry.panelName);
  }

  private pushFragment(page: TabPage) {
    if (!page.fragment) return;
    // use history.pushState instead of window.location.hash as the latter reloads the page
    const target = `${window.location.pathname}#${page.fragment}`;
    // Why guard against an identical URL? Because when a page is deep-linked
    // via Router.go('/foo#frag'), the router pushes the URL and then this
    // activation runs and would push the same URL again - one Back press
    // would then visibly do nothing.
    if (target !== `${window.location.pathname}${window.location.hash}`) {
      history.pushState(null, '', target);
    }
  }

  private async runAction(action: TabAction) {
    if (action.disabled || this.uiLocked) return;
    action.disabled = true;
    this.lockUI(true);
    try {
      await action.onClick?.();
    } finally {
      action.disabled = false;
      this.lockUI(false);
      this.requestUpdate();
    }
  }

  private async runAddAction(action: TabAction) {
    if (action.disabled || this.uiLocked) return;
    this.lockUI(true);
    try {
      if (!(await this.canLeavePage(this.activePage))) return;
      action.disabled = true;
      this.requestUpdate();
      try {
        await action.onClick?.();
      } finally {
        action.disabled = false;
        this.requestUpdate();
      }
    } finally {
      this.lockUI(false);
    }
  }

  private async canLeavePage(page: TabPage | null): Promise<boolean> {
    return page === null || (page.canLeave ? await page.canLeave(page) : true);
  }

  private lockUI(lock: boolean) {
    if (lock) {
      disableUI();
      this.lockCount++;
    } else {
      enableUI();
      this.lockCount--;
    }
    if (this.lockCount < 0) throw new DevelopmentError('TabManager lock out of sync');
  }

  private rangeTest(index: number) {
    if (index >= 0 && index < this.entries.length) return;
    throw new Error(`Page index ${index} out of range [0,${this.entries.length - 1}]`);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wm-tab-manager': TabManager;
  }
}
